/**
 * 路由配置文件
 * 定义全部页面路由、嵌套子路由，以及全局导航守卫（登录鉴权）。
 */
import { createRouter, createWebHistory } from "vue-router";
import LoginView from "@/views/LoginView.vue";
import NoAccessView from "@/views/NoAccessView.vue";
import BasicLayout from "@/layouts/BasicLayout.vue";
import DashboardView from "@/views/DashboardView.vue";
import OrderDailyView from "@/views/order/OrderDailyView.vue";
import AppOrderDailyView from "@/views/order/AppOrderDailyView.vue";
import AppOrderMonthlyView from "@/views/order/AppOrderMonthlyView.vue";
import OrderHourlyPaymentView from "@/views/order/OrderHourlyPaymentView.vue";
import AgreementRecordsView from "@/views/order/AgreementRecordsView.vue";
import OrderRecordsView from "@/views/order/OrderRecordsView.vue";
import OrderLinkStatsView from "@/views/order/OrderLinkStatsView.vue";
import CustomerServiceStatsView from "@/views/order/CustomerServiceStatsView.vue";
import HlbServiceTransferStatsView from "@/views/order/HlbServiceTransferStatsView.vue";
import OrderLtvView from "@/views/order/OrderLtvView.vue";
import OrderLtvV2View from "@/views/order/OrderLtvV2View.vue";
import PayRefundDailyView from "@/views/order/PayRefundDailyView.vue";
import OrderDailyStatView from "@/views/order/OrderDailyStatView.vue";
import DeductionTagDailyView from "@/views/order/DeductionTagDailyView.vue";
import DeductionForecastV2View from "@/views/order/DeductionForecastV2View.vue";
import UserManageView from "@/views/system/UserManageView.vue";
import RoleManageView from "@/views/system/RoleManageView.vue";
import UserQueryView from "@/views/operate/UserQueryView.vue";
import ExternalAgreementConfView from "@/views/operate/ExternalAgreementConfView.vue";
import OceanEngineConversionView from "@/views/operate/OceanEngineConversionView.vue";
import FusionPaymentConfigView from "@/views/operate/FusionPaymentConfigView.vue";
import AdPlatformAppView from "@/views/operate/AdPlatformAppView.vue";
import AgreementIpBlockView from "@/views/operate/AgreementIpBlockView.vue";
import { authStore } from "@/store/auth";
import { hasMenu } from "@/utils/permission";

/** 所有菜单路径（用于查找第一个有权限的页面） */
const allMenuPaths = [
  "/dashboard",
  "/order/daily",
  "/order/app-daily",
  "/order/hourly-payment",
  "/order/link-stats",
  "/order/customer-service-stats",
  "/order/hlb-service-transfer-stats",
  "/order/app-monthly",
  "/order/ltv",
  "/order/ltv-v2",
  "/order/pay-refund-daily",
  "/order/daily-stat",
  "/order/deduction-tag-daily",
  "/order/deduction-forecast-v2",
  "/order/pay-orders",
  "/order/payment-records",
  "/order/airwallex",
  "/order/hlb",
  "/order/kq",
  "/system/user",
  "/system/role",
  "/operate/user-query",
  "/operate/external-agreement-conf",
  "/operate/ocean-engine-conversion",
  "/operate/fusion-payment-config",
  "/operate/ad-platform-app",
  "/operate/agreement-ip-block",
  "/docs/flow-events",
];
const getFirstAllowedRoute = () => allMenuPaths.find((path) => hasMenu(path)) || "/no-access";

/**
 * 路由表
 * - /login         登录页（meta.public = true 跳过鉴权）
 * - /              后台布局容器（BasicLayout），重定向到首个有效菜单或 /no-access
 *   - no-access    已登录但无有效菜单权限时的提示页
 *   - dashboard    控制台
 *   - order/*      订单统计子页面
 * - /:pathMatch    404 兜底，重定向到首个有效菜单或 /no-access
 */
const routes = [
  {
    path: "/login",
    name: "Login",
    component: LoginView,
    meta: { public: true, title: "登录" }, // 公开页面，无需登录
  },
  {
    path: "/",
    component: BasicLayout,
    redirect: getFirstAllowedRoute,
    children: [
      {
        path: "no-access",
        name: "NoAccess",
        component: NoAccessView,
        meta: { title: "暂无可访问页面" },
      },
      {
        path: "dashboard",
        name: "Dashboard",
        component: DashboardView,
        meta: { title: "控制台" },
      },
      {
        path: "order/daily",
        name: "OrderDaily",
        component: OrderDailyView,
        meta: { title: "全收款统计" },
      },
      {
        path: "order/app-daily",
        name: "AppOrderDaily",
        component: AppOrderDailyView,
        meta: { title: "日收款统计" },
      },
      {
        path: "order/hourly-payment",
        name: "OrderHourlyPayment",
        component: OrderHourlyPaymentView,
        meta: { title: "小时统计" },
      },
      {
        path: "order/link-stats",
        name: "OrderLinkStats",
        component: OrderLinkStatsView,
        meta: { title: "链接统计" },
      },
      {
        path: "order/customer-service-stats",
        name: "CustomerServiceStats",
        component: CustomerServiceStatsView,
        meta: { title: "客服统计" },
      },
      {
        path: "order/hlb-service-transfer-stats",
        name: "HlbServiceTransferStats",
        component: HlbServiceTransferStatsView,
        meta: { title: "合利宝转接" },
      },
      {
        path: "order/app-monthly",
        name: "AppOrderMonthly",
        component: AppOrderMonthlyView,
        meta: { title: "月收款统计" },
      },
      {
        path: "order/ltv",
        name: "OrderLtv",
        component: OrderLtvView,
        meta: { title: "LTV报告" },
      },
      {
        path: "order/ltv-v2",
        name: "OrderLtvV2",
        component: OrderLtvV2View,
        meta: { title: "LTV-维度" },
      },
      {
        path: "order/pay-refund-daily",
        name: "PayRefundDaily",
        component: PayRefundDailyView,
        meta: { title: "收退款-财务" },
      },
      {
        path: "order/daily-stat",
        name: "OrderDailyStat",
        component: OrderDailyStatView,
        meta: { title: "每日订单" },
      },
      {
        path: "order/deduction-tag-daily",
        name: "OrderDeductionTagDaily",
        component: DeductionTagDailyView,
        meta: { title: "预扣款统计" },
      },
      {
        path: "order/deduction-forecast-v2",
        name: "OrderDeductionForecastV2",
        component: DeductionForecastV2View,
        meta: { title: "预扣款统计V2" },
      },
      {
        path: "order/pay-orders",
        name: "PayOrderRecords",
        component: OrderRecordsView,
        props: { kind: "pay-orders" },
        meta: { title: "下单记录" },
      },
      {
        path: "order/payment-records",
        name: "PaymentRecords",
        component: OrderRecordsView,
        props: { kind: "payment-records" },
        meta: { title: "支付记录" },
      },
      {
        path: "order/airwallex",
        name: "AirwallexAgreement",
        component: AgreementRecordsView,
        props: { provider: "airwallex" },
        meta: { title: "空中云汇" },
      },
      {
        path: "order/hlb",
        name: "HlbAgreement",
        component: AgreementRecordsView,
        props: { provider: "hlb" },
        meta: { title: "合利宝" },
      },
      {
        path: "order/kq",
        name: "KqAgreement",
        component: AgreementRecordsView,
        props: { provider: "kq" },
        meta: { title: "快钱" },
      },
      {
        path: "system/user",
        name: "SystemUser",
        component: UserManageView,
        meta: { title: "用户管理" },
      },
      {
        path: "system/role",
        name: "SystemRole",
        component: RoleManageView,
        meta: { title: "角色权限" },
      },
      {
        path: "operate/user-query",
        name: "OperateUserQuery",
        component: UserQueryView,
        meta: { title: "客服查询" },
      },
      {
        path: "operate/external-agreement-conf",
        name: "OperateExternalAgreementConf",
        component: ExternalAgreementConfView,
        meta: { title: "外部配置" },
      },
      {
        path: "operate/ocean-engine-conversion",
        name: "OperateOceanEngineConversion",
        component: OceanEngineConversionView,
        meta: { title: "巨量回传" },
      },
      {
        path: "operate/fusion-payment-config",
        name: "OperateFusionPaymentConfig",
        component: FusionPaymentConfigView,
        meta: { title: "融合支付配置" },
      },
      {
        path: "operate/ad-platform-app",
        name: "OperateAdPlatformApp",
        component: AdPlatformAppView,
        meta: { title: "投放媒体" },
      },
      {
        path: "operate/agreement-ip-block",
        name: "OperateAgreementIpBlock",
        component: AgreementIpBlockView,
        meta: { title: "签约ip屏蔽" },
      },
      {
        path: "docs/flow-events",
        name: "FlowEvents",
        component: () => import("@/views/docs/FlowEventsView.vue"),
        meta: { title: "Flow打点" },
      },
    ],
  },
  {
    // 未匹配路由兜底：重定向到第一个有权限的页面
    path: "/:pathMatch(.*)*",
    redirect: getFirstAllowedRoute,
  },
];

const router = createRouter({
  history: createWebHistory(), // HTML5 History 模式
  routes,
});

/**
 * 全局前置守卫
 * 1. 未登录 → 跳转登录页
 * 2. 已登录访问登录页 → 跳转第一个有权限页面
 * 3. 已登录但无有效菜单权限 → /no-access 提示页
 */
router.beforeEach((to, _from, next) => {
  const isAuthenticated = authStore.isLoggedIn.value;

  if (!to.meta.public && !isAuthenticated) {
    next({ name: "Login" });
  } else if (to.name === "Login" && isAuthenticated) {
    next({ path: getFirstAllowedRoute() });
  } else if (to.name === "NoAccess" && isAuthenticated) {
    const first = getFirstAllowedRoute();
    if (first === "/no-access") next();
    else next({ path: first });
  } else if (isAuthenticated && to.path !== "/" && !to.meta.public) {
    // 检查菜单权限（"/" 是重定向路由，跳过检查）
    const menuPath = "/" + to.path.replace(/^\//, "");
    if (!hasMenu(menuPath)) {
      const first = getFirstAllowedRoute();
      if (menuPath !== first) {
        next({ path: first });
        return;
      }
    }
    next();
  } else {
    next();
  }

  document.title = to.meta.title
    ? to.meta.title + " - 数据统计平台"
    : "数据统计平台";
});

export default router;
